import { useEffect, useRef, useState } from "react";
import type React from "react";
import { theme, btnPrimary, btnSecondary, type as text } from "../theme";
import { apiErrorMessage } from "../api/client";
import { runAiJob, pollAiJob, rememberAiJob, forgetAiJob, recalledAiJobs } from "../lib/aiJob";

// An AI draft of a corrective action's root cause (backend lib/rootCauseDraft.ts). The person
// working the action asks; the worker drafts a five-whys chain or a fishbone from the records the
// action is linked to, each statement citing the numbered records it rests on. The draft is shown
// here and only goes into the editor when the person chooses to use it; nothing is saved until
// they save the action, which then records that the root cause started from an AI draft.

interface DraftCause { text: string; cites: string[] }
type Draft =
  | { method: "five_whys"; problem: string; whys: DraftCause[] }
  | { method: "fishbone"; problem: string; categories: Array<{ category: string; causes: DraftCause[] }>; decisive: DraftCause | null };
interface DraftResult {
  draft: Draft;
  text: string;
  sources: Array<{ ref: string; kind: string; label: string }>;
  droppedCitations: number;
}

const DRAFTABLE = ["five_whys", "fishbone"];
const CATEGORY_LABELS: Record<string, string> = {
  people: "People", process: "Process", technology: "Technology", suppliers: "Suppliers",
  environment: "Environment", management: "Management and governance",
};
const jobKey = (actionId: string) => `root-cause:${actionId}`;
const statusPath = (jobId: string) => `/improvement/root-cause-draft-jobs/${jobId}`;

const box: React.CSSProperties = {
  marginTop: 10, padding: "10px 12px", border: "1px dashed var(--c-borderStrong)", borderRadius: theme.radius.sm,
  background: "var(--c-panelAlt)",
};
const small: React.CSSProperties = { ...btnSecondary, padding: "5px 12px", fontSize: 12 };
const smallPrimary: React.CSSProperties = { ...btnPrimary, padding: "5px 12px", fontSize: 12 };

function Cites({ cites, sources }: { cites: string[]; sources: DraftResult["sources"] }) {
  if (!cites.length) return null;
  return (
    <span style={{ marginLeft: 6 }}>
      {cites.map((c) => (
        <span
          key={c}
          title={sources.find((s) => s.ref === c)?.label ?? c}
          style={{ ...text.ref, color: "var(--c-ref)", border: "1px solid var(--c-border)", borderRadius: 2, padding: "0 4px", marginLeft: 3 }}
        >
          {c}
        </span>
      ))}
    </span>
  );
}

export function RootCauseDraftPanel({ actionId, method, hasText, onUse }: {
  actionId: string;
  /** The method chosen in the editor; the draft is written in it. */
  method: string;
  /** The editor already holds a root cause, so using the draft replaces it. */
  hasText: boolean;
  onUse: (v: { method: string; text: string }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<DraftResult | null>(null);
  const abort = useRef<AbortController | null>(null);

  const follow = async (p: Promise<DraftResult>) => {
    setBusy(true); setError("");
    try {
      setResult(await p);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setError(apiErrorMessage(e, "The draft could not be written."));
    } finally {
      forgetAiJob(jobKey(actionId));
      setBusy(false);
    }
  };

  // A draft asked for before navigating away is picked up again.
  useEffect(() => {
    const ctrl = new AbortController();
    abort.current = ctrl;
    const recalled = recalledAiJobs(jobKey(actionId)).find((j) => j.key === jobKey(actionId));
    if (recalled) void follow(pollAiJob<DraftResult>(recalled.jobId, statusPath, { signal: ctrl.signal, waitMs: recalled.remainingMs }));
    return () => ctrl.abort();
  }, [actionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ask = () => {
    setResult(null);
    void follow(runAiJob<DraftResult>(`/improvement/actions/${actionId}/root-cause-draft`, statusPath, { method }, {
      signal: abort.current?.signal,
      onJobId: (jobId) => rememberAiJob(jobKey(actionId), jobId),
    }));
  };

  const draftable = DRAFTABLE.includes(method);
  const d = result?.draft;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={ask} disabled={!draftable || busy} style={{ ...small, opacity: !draftable || busy ? 0.6 : 1 }}>
          {busy ? "Drafting…" : result ? "Draft again" : "Draft with AI"}
        </button>
        <span style={text.hint}>
          {draftable
            ? "The AI drafts it from the incident, its log and review, the findings, failed record checks and the linked risk, supplier and asset. You edit and save it; nothing is saved until you do."
            : "Choose five whys or fishbone to ask the AI for a draft."}
        </span>
      </div>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginTop: 6 }}>{error}</div>}

      {result && d && (
        <div style={box} aria-label="AI draft of the root cause">
          <div style={{ ...text.eyebrow, marginBottom: 6 }}>AI draft, not saved</div>
          {d.problem && <div style={{ ...text.body, marginBottom: 6 }}><strong>Problem:</strong> {d.problem}</div>}
          {d.method === "five_whys" ? (
            <ol style={{ ...text.body, margin: 0, paddingLeft: 20, display: "grid", gap: 4 }}>
              {d.whys.map((w, i) => <li key={i}>Why? {w.text}<Cites cites={w.cites} sources={result.sources} /></li>)}
            </ol>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {d.categories.map((c) => (
                <div key={c.category}>
                  <div style={{ ...text.label, marginBottom: 2 }}>{CATEGORY_LABELS[c.category] ?? c.category}</div>
                  <ul style={{ ...text.body, margin: 0, paddingLeft: 20 }}>
                    {c.causes.map((x, i) => <li key={i}>{x.text}<Cites cites={x.cites} sources={result.sources} /></li>)}
                  </ul>
                </div>
              ))}
              {d.decisive && <div style={text.body}><strong>Decisive cause:</strong> {d.decisive.text}<Cites cites={d.decisive.cites} sources={result.sources} /></div>}
            </div>
          )}
          <div style={{ ...text.meta, marginTop: 8 }}>
            Drawn from: {result.sources.map((s) => `${s.ref} ${s.label}`).join("; ")}.
            {result.droppedCitations > 0 && ` ${result.droppedCitations} citation${result.droppedCitations === 1 ? "" : "s"} to records that were not supplied ${result.droppedCitations === 1 ? "was" : "were"} removed.`}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button type="button" style={smallPrimary} onClick={() => { onUse({ method: d.method, text: result.text }); setResult(null); }}>
              {hasText ? "Replace the root cause with this draft" : "Use this draft"}
            </button>
            <button type="button" style={small} onClick={() => setResult(null)}>Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
